import {
    Box,
    Button,
    Chip,
    Divider,
    List,
    ListItem,
    ListItemText,
    Modal,
    Paper,
    Typography
} from "@mui/material";

export interface ImportPreviewTask {
    title: string;
    language?: string;
    testCasesCount: number;
}

interface Props {
    open: boolean;
    fileName: string;
    tasks: ImportPreviewTask[];
    importing: boolean;
    onClose: () => void;
    onConfirm: () => void;
}

const ImportCodeTasksPreviewModal = ({
                                         open,
                                         fileName,
                                         tasks,
                                         importing,
                                         onClose,
                                         onConfirm
                                     }: Props) => {
    return (
        <Modal open={open} onClose={importing ? undefined : onClose}>
            <Box
                sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    transform: "translate(-50%, -50%)",
                    width: "min(720px, 94vw)",
                    maxHeight: "90vh",
                    outline: "none",
                }}
            >
                <Paper
                    elevation={1}
                    sx={(theme) => ({
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: 2,
                        overflow: "hidden",
                    })}
                >
                    <Box
                        sx={(theme) => ({
                            px: 3,
                            py: 2,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Typography variant="h6" sx={{fontWeight: 700}}>
                            Import Code Tasks
                        </Typography>

                        <Typography variant="body2" color="text.secondary" sx={{mt: 0.5}}>
                            Please review tasks before importing.
                        </Typography>
                    </Box>

                    <Box sx={{px: 3, py: 2}}>
                        <Box sx={{mb: 2}}>
                            <Typography variant="body2" color="text.secondary">
                                File
                            </Typography>

                            <Typography variant="subtitle2" sx={{wordBreak: "break-all"}}>
                                {fileName}
                            </Typography>
                        </Box>

                        <Box
                            sx={(theme) => ({
                                mb: 2,
                                p: 1.5,
                                borderRadius: 1.5,
                                backgroundColor: theme.palette.action.hover,
                            })}
                        >
                            <Typography variant="body2">
                                This file contains <strong>{tasks.length}</strong> code task(s).
                                They will be appended after existing tasks in this lesson.
                            </Typography>
                        </Box>

                        <Divider sx={{mb: 1}}/>

                        <List
                            dense
                            sx={{
                                maxHeight: 360,
                                overflow: "auto",
                                p: 0,
                            }}
                        >
                            {tasks.map((task, index) => (
                                <ListItem
                                    key={`${task.title}-${index}`}
                                    sx={(theme) => ({
                                        px: 0,
                                        borderBottom: `1px solid ${theme.palette.divider}`,
                                    })}
                                >
                                    <ListItemText
                                        primary={
                                            <Box sx={{display: "flex", alignItems: "center", gap: 1}}>
                                                <Typography variant="body2" sx={{fontWeight: 600}}>
                                                    {index + 1}. {task.title}
                                                </Typography>

                                                {task.language && (
                                                    <Chip
                                                        label={task.language}
                                                        size="small"
                                                        variant="outlined"
                                                    />
                                                )}
                                            </Box>
                                        }
                                        secondary={`${task.testCasesCount} test case(s)`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                    </Box>

                    <Box
                        sx={(theme) => ({
                            px: 3,
                            py: 2,
                            display: "flex",
                            justifyContent: "flex-end",
                            gap: 1,
                            borderTop: `1px solid ${theme.palette.divider}`,
                        })}
                    >
                        <Button
                            onClick={onClose}
                            variant="outlined"
                            disabled={importing}
                            sx={{textTransform: "none"}}
                        >
                            Cancel
                        </Button>

                        <Button
                            onClick={onConfirm}
                            variant="contained"
                            disabled={importing}
                            sx={{textTransform: "none"}}
                        >
                            {importing ? "Importing..." : "Confirm Import"}
                        </Button>
                    </Box>
                </Paper>
            </Box>
        </Modal>
    );
};

export default ImportCodeTasksPreviewModal;